import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { IconCheck, IconInfo, IconRefresh } from './Icons';
import '../pages/NinjaTraderIntegration.css';
import './TradovatePanel.css';

function TradovatePanel({ user, embedded = false, hideAccountsTable = false, onSessionChange }) {
  const [statusLoading, setStatusLoading] = useState(true);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [connected, setConnected] = useState(false);
  const [environment, setEnvironment] = useState('live');
  const [serverEnv, setServerEnv] = useState(null);
  const [tradovateUserId, setTradovateUserId] = useState(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [error, setError] = useState(null);
  const [banner, setBanner] = useState(null);
  const [connectBusy, setConnectBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    if (!user?.email) {
      setStatusLoading(false);
      return { connected: false };
    }
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/tradovate/status`, {
        headers: { ...getBearerAuthHeaders() },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 404) {
          setConfigured(false);
          setConnected(false);
          setError('Tradovate API not found. Deploy the latest API with /api/tradovate routes enabled.');
          return { connected: false };
        }
        throw new Error(data.error || 'Failed to load Tradovate status');
      }
      setConfigured(data.configured !== false);
      const isConnected = !!data.connected;
      setConnected(isConnected);
      setServerEnv(data.environment || null);
      setTradovateUserId(data.tradovateUserId ?? null);
      setTokenExpiresAt(data.tokenExpiresAt || null);
      return { connected: isConnected };
    } catch (e) {
      const msg = e.message || 'Status request failed';
      setError(/404|not found/i.test(msg) ? 'Tradovate API is not available (check deployment and /api/tradovate routes).' : msg);
      setConnected(false);
      return { connected: false };
    } finally {
      setStatusLoading(false);
    }
  }, [user?.email]);

  const loadAccounts = useCallback(async () => {
    if (!user?.email) return;
    setAccountsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/tradovate/accounts`, {
        headers: { ...getBearerAuthHeaders() },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || data.error || 'Failed to load accounts');
      }
      setAccounts(Array.isArray(data.accounts) ? data.accounts : []);
    } catch (e) {
      setError(e.message || 'Accounts request failed');
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStatusLoading(true);
      const params = new URLSearchParams(window.location.search);
      const tv = params.get('tradovate');
      const reason = params.get('reason');
      if (tv === 'success' || tv === 'error') {
        window.history.replaceState({}, '', window.location.pathname);
        if (tv === 'success') {
          setBanner({ type: 'ok', text: 'Tradovate connected successfully.' });
        } else {
          setBanner({
            type: 'err',
            text: reason ? `Tradovate connection failed: ${decodeURIComponent(reason)}` : 'Tradovate connection failed.',
          });
        }
      }
      const { connected: isConnected } = await loadStatus();
      if (!cancelled && isConnected) {
        await loadAccounts();
      }
      if (!cancelled && tv === 'success') {
        onSessionChange?.();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadStatus, loadAccounts, onSessionChange]);

  useEffect(() => {
    if (!connected) {
      setAccounts([]);
    }
  }, [connected]);

  const handleConnect = async () => {
    if (!user?.email) return;
    setConnectBusy(true);
    setError(null);
    try {
      const frontendUrl = `${window.location.origin}${window.location.pathname}`;
      const res = await fetch(`${API_BASE_URL}/api/tradovate/oauth/prepare`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getBearerAuthHeaders(),
        },
        body: JSON.stringify({ frontendUrl, environment }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.authUrl) {
        if (res.status === 404) {
          throw new Error('Tradovate API not found. Deploy the latest server with /api/tradovate routes.');
        }
        throw new Error(data.error || 'Could not start Tradovate sign-in');
      }
      window.location.href = data.authUrl;
    } catch (e) {
      setError(e.message || 'Connect failed');
    } finally {
      setConnectBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!user?.email) return;
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/tradovate/disconnect`, {
        method: 'POST',
        headers: { ...getBearerAuthHeaders() },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Disconnect failed');
      }
      setConnected(false);
      setAccounts([]);
      setServerEnv(null);
      setTradovateUserId(null);
      setTokenExpiresAt(null);
      onSessionChange?.();
    } catch (e) {
      setError(e.message || 'Disconnect failed');
    }
  };

  const handleRefresh = async () => {
    setStatusLoading(true);
    const { connected: isConnected } = await loadStatus();
    if (isConnected) {
      await loadAccounts();
    }
  };

  if (!user?.email) {
    return (
      <div className={`tradovate-panel ${embedded ? 'tradovate-panel-embedded' : ''}`}>
        <p className="results-dash-subtitle">Sign in to connect Tradovate.</p>
      </div>
    );
  }

  return (
    <div className={`tradovate-panel ${embedded ? 'tradovate-panel-embedded' : ''}`}>
      {banner && (
        <div className={`tradovate-banner tradovate-banner-${banner.type}`} role="status">
          {banner.text}
        </div>
      )}

      <div className="results-controls-row tradovate-controls" style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap', width: embedded ? '100%' : undefined }}>
        <div className="results-select-group">
          <label className="results-select-label" style={{ textAlign: 'left' }}>
            Environment
          </label>
          <select
            className="results-sheet-select-inline"
            aria-label="Tradovate environment"
            value={environment}
            onChange={(e) => setEnvironment(e.target.value)}
            disabled={connectBusy || !configured}
          >
            <option value="live">Live</option>
            <option value="demo">Demo</option>
          </select>
        </div>
        <button
          type="button"
          className="nt-btn nt-btn-secondary"
          onClick={handleConnect}
          disabled={connectBusy || !configured || statusLoading}
        >
          {connectBusy ? 'Redirecting…' : 'Connect Tradovate'}
        </button>
        {connected && (
          <>
            <button type="button" className="nt-btn nt-btn-secondary" onClick={handleRefresh} disabled={statusLoading}>
              <IconRefresh size={16} /> Refresh
            </button>
            <button type="button" className="nt-btn nt-btn-outline-danger" onClick={handleDisconnect}>
              Disconnect
            </button>
          </>
        )}
      </div>

      {!embedded && (
        <>
          <p className="results-dash-week">Tradovate</p>
          <p className="results-dash-subtitle">
            Connect your Tradovate account with OAuth. After you authorize, RiskLo can list your Tradovate accounts here.
          </p>
        </>
      )}
      {embedded && (
        <p className="results-dash-subtitle" style={{ marginTop: '0.5rem', marginBottom: '0.35rem' }}>
          Connect Tradovate — accounts appear in the list below with your NinjaTrader accounts.
        </p>
      )}

      {!configured && (
        <div className="tradovate-hint" role="note">
          <IconInfo size={16} />
          <span>
            Tradovate integration is not configured on this server yet. Add TRADOVATE_CLIENT_ID, TRADOVATE_CLIENT_SECRET, and
            TRADOVATE_REDIRECT_URI to the API environment.
          </span>
        </div>
      )}

      {statusLoading ? (
        <p className="tradovate-muted">Loading connection status…</p>
      ) : connected ? (
        <div className="tradovate-status tradovate-status-connected">
          <IconCheck size={18} />
          <span>
            Connected ({serverEnv === 'demo' ? 'Demo' : 'Live'}
            {tradovateUserId != null ? ` · user ${tradovateUserId}` : ''}
            {tokenExpiresAt ? ` · token expires ${new Date(tokenExpiresAt).toLocaleString()}` : ''})
          </span>
        </div>
      ) : (
        <p className="tradovate-muted">Not connected.</p>
      )}

      {error && (
        <div className="tradovate-error" role="alert">
          {error}
        </div>
      )}

      {connected && !hideAccountsTable && (
        <div className="tradovate-accounts-wrap">
          <h3 className="tradovate-accounts-heading">Accounts</h3>
          {accountsLoading ? (
            <p className="tradovate-muted">Loading accounts…</p>
          ) : accounts.length === 0 ? (
            <p className="tradovate-muted">No accounts returned.</p>
          ) : (
            <div className="tradovate-table-scroll">
              <table className="tradovate-accounts-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>ID</th>
                    <th>Type</th>
                    <th>Active</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a) => (
                    <tr key={a.id ?? a.name}>
                      <td>{a.name}</td>
                      <td>{a.id}</td>
                      <td>{a.accountType || '—'}</td>
                      <td>{a.active ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TradovatePanel;
